import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Package, Info } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import {
  useAvailableOrdersForInvoice,
  useAddOrdersToInvoice,
} from "@/api/invoices";

interface AddOrdersToInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  onSuccess?: () => void;
}

export function AddOrdersToInvoiceDialog({
  open,
  onOpenChange,
  invoiceId,
  onSuccess,
}: AddOrdersToInvoiceDialogProps) {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);

  // Fetch available orders
  const { data: ordersResponse, isLoading } = useAvailableOrdersForInvoice(
    open ? invoiceId : null
  );

  // Add orders mutation
  const addOrders = useAddOrdersToInvoice();

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedOrders([]);
    }
  }, [open]);

  const handleToggleOrder = (orderId: string) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (!ordersResponse?.data) return;
    if (selectedOrders.length === ordersResponse.data.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(ordersResponse.data.map((o) => o.id));
    }
  };

  const handleAddOrders = () => {
    if (selectedOrders.length === 0) {
      toast.error("No Orders Selected", {
        description: "Please select at least one order to add",
      });
      return;
    }

    addOrders.mutate(
      { invoiceId, orderIds: selectedOrders },
      {
        onSuccess: (data) => {
          toast.success("Orders Added", {
            description: `Successfully added ${data.addedCount} order(s) to the invoice`,
          });
          onSuccess?.();
          onOpenChange(false);
        },
        onError: (
          error: Error & { response?: { data?: { error?: string } } }
        ) => {
          toast.error("Failed to Add Orders", {
            description: error.response?.data?.error || "An error occurred",
          });
        },
      }
    );
  };

  const orders = ordersResponse?.data ?? [];
  const selectedTotal = orders
    .filter((o) => selectedOrders.includes(o.id))
    .reduce((sum, o) => sum + (o.totalCents || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Orders to Invoice</DialogTitle>
          <DialogDescription>
            Select completed orders to add to this draft invoice
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : orders.length === 0 ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                No eligible orders found. Orders must be service-completed with
                CREDIT_APPROVED payment status and not yet on an invoice.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="select-all"
                    checked={selectedOrders.length === orders.length}
                    onCheckedChange={handleSelectAll}
                  />
                  <label
                    htmlFor="select-all"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Select All ({orders.length} orders)
                  </label>
                </div>
                {selectedOrders.length > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {selectedOrders.length} selected -{" "}
                    {formatCurrency(selectedTotal)}
                  </span>
                )}
              </div>

              <div className="space-y-2 border rounded-md divide-y">
                {orders.map((order) => {
                  const destination = order.shipments[0]?.addresses[0];
                  return (
                    <label
                      key={order.id}
                      className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={selectedOrders.includes(order.id)}
                        onCheckedChange={() => handleToggleOrder(order.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-medium">
                            ORD-{order.orderNumber}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {formatCurrency(order.totalCents || 0)}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground truncate">
                          {destination?.contactName && (
                            <span>{destination.contactName}</span>
                          )}
                          {destination?.address && (
                            <span>
                              {destination.contactName ? " - " : ""}
                              {destination.address.city},{" "}
                              {destination.address.province}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Created:{" "}
                          {new Date(order.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={addOrders.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddOrders}
            disabled={addOrders.isPending || selectedOrders.length === 0}
          >
            {addOrders.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Add {selectedOrders.length > 0 && `${selectedOrders.length} `}
            Order{selectedOrders.length !== 1 && "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
